import { SERVICE_ORDER, SERVICE_LABELS, SERVICE_DESCRIPTIONS, SERVICE_COLORS } from "../constants";
import { formatMs } from "../utils";

export default function PipelineFlow({ metricsStats, totalPipelineMs }) {
  // Calculate percentages for all services (in pipeline order)
  const serviceData = SERVICE_ORDER.map((service) => {
    const avgMs = metricsStats?.avg_ms?.[service] || 0;
    const pct = totalPipelineMs ? (avgMs / totalPipelineMs) * 100 : 25;
    return { service, avgMs, pct, color: SERVICE_COLORS[service] };
  });

  return (
    <div className="pipeline-flow">
      <h3>
        <span className="flow-icon">&gt;</span>
        Processing Time Distribution
      </h3>

      {/* Proportional stacked bar - in pipeline order */}
      <div className="flow-bar-container">
        <div className="flow-bar-track">
          {serviceData.map(({ service, pct, color }) => {
            // For tiny percentages (<2%), show as a thin sliver with popout
            const isTiny = pct < 2;
            const isSmall = pct >= 2 && pct < 8;
            // Visual width: tiny gets fixed 4px, small gets minimum, rest proportional
            const visualPct = (() => {
              if (isTiny) return 0.3;
              if (isSmall) return Math.max(pct, 4);
              return pct;
            })();

            const sizeClass = (() => {
              if (isTiny) return "tiny";
              if (isSmall) return "small";
              return "";
            })();

            const minWidthValue = (() => {
              if (isTiny) return "4px";
              if (isSmall) return "50px";
              return "auto";
            })();

            return (
              <div
                key={service}
                className={`flow-bar-segment ${sizeClass}`}
                style={{
                  flex: visualPct,
                  background: isTiny ? color : `linear-gradient(135deg, ${color}, ${color}cc)`,
                  boxShadow: `inset 0 1px 0 ${color}40`,
                  minWidth: minWidthValue,
                }}
                title={`${SERVICE_LABELS[service]}: ${pct.toFixed(1)}% (${formatMs(metricsStats?.avg_ms?.[service])})`}
              >
                {!isTiny && (
                  <>
                    <span className="segment-label">{SERVICE_LABELS[service]}</span>
                    <span className="segment-pct">{pct.toFixed(0)}%</span>
                  </>
                )}
                {/* Popout for tiny segments */}
                {isTiny && (
                  <div className="segment-popout" style={{ borderColor: color }}>
                    <span className="popout-name">{SERVICE_LABELS[service]}</span>
                    <span className="popout-pct" style={{ color }}>
                      {pct.toFixed(1)}%
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend with times */}
      <div className="flow-legend">
        {serviceData.map(({ service, avgMs, pct, color }) => (
          <div key={service} className="legend-item">
            <div className="legend-color" style={{ background: color }} />
            <div className="legend-info">
              <span className="legend-name">{SERVICE_LABELS[service]}</span>
              <span className="legend-desc">{SERVICE_DESCRIPTIONS[service]}</span>
            </div>
            <div className="legend-stats">
              <span className="legend-time" style={{ color }}>
                {formatMs(avgMs)}
              </span>
              <span className="legend-pct">{pct.toFixed(1)}%</span>
            </div>
          </div>
        ))}
      </div>

      {/* Total */}
      <div className="flow-total">
        <span className="total-label">Total Pipeline Time:</span>
        <span className="total-value">{formatMs(totalPipelineMs)}</span>
      </div>
    </div>
  );
}
